import { useEffect, useRef, useContext } from "react";
import { EchoContext } from "./EchoContext";
import { ConnectionState, UseEchoSubscriptionOptions, UseEchoSubscriptionReturn } from "./types";

// Debug mode - apenas log em desenvolvimento
const DEBUG = import.meta.env.DEV;
const log = (...args: any[]) => {
    if (DEBUG) console.log('[useEchoSubscription]', ...args);
};

/**
 * Hook genérico para subscrever a eventos WebSocket via Laravel Echo
 *
 * @param options Opções de configuração da subscrição
 * @returns Estado da conexão e instância do Echo
 *
 * @example
 * ```tsx
 * // Subscrição simples a um canal
 * const { connectionState } = useEchoSubscription({
 *   channelName: "mesas.1",
 *   events: {
 *     MesaAtualizada: (data) => console.log("Mesa atualizada:", data),
 *     MesaLiberada: (data) => console.log("Mesa liberada:", data),
 *   },
 *   onConnectionRestored: () => {
 *     console.log("Conexão restaurada, recarregando dados...");
 *     fetchData();
 *   },
 * });
 *
 * // Subscrição condicional
 * const { connectionState } = useEchoSubscription({
 *   channelName: `notificacoes.user.${userId}`,
 *   events: {
 *     NovaNotificacao: handleNotificacao,
 *   },
 *   enabled: !!userId, // Só subscreve se userId existir
 * });
 * ```
 */
export const useEchoSubscription = (options: UseEchoSubscriptionOptions): UseEchoSubscriptionReturn => {
    const { channelName, events, enabled = true, onConnectionRestored, onConnectionError } = options;

    // Usar o contexto Echo
    const echoContext = useContext(EchoContext);

    log(`[INIT] echoContext existe: ${!!echoContext}, connectionState: ${echoContext?.connectionState ?? 'N/A'}`);

    // Guardar os callbacks mais recentes em refs para evitar problemas de dependências
    const eventsRef = useRef(events);
    const onConnectionRestoredRef = useRef(onConnectionRestored);
    const onConnectionErrorRef = useRef(onConnectionError);
    const previousConnectionStateRef = useRef<ConnectionState>(
        echoContext?.connectionState ?? ConnectionState.DISCONNECTED
    );

    // Se não houver EchoProvider, retornar versão no-op (sem atualizações em tempo real)
    // Isso permite que os componentes funcionem sem tempo real quando usados fora do provider
    if (!echoContext) {
        log("[WARN] useEchoSubscription usado fora do EchoProvider - atualizações em tempo real desativadas");
        return {
            echo: null,
            connectionState: ConnectionState.DISCONNECTED
        };
    }

    // Atualizar refs quando as opções mudam
    useEffect(() => {
        eventsRef.current = events;
        onConnectionRestoredRef.current = onConnectionRestored;
        onConnectionErrorRef.current = onConnectionError;
    }, [events, onConnectionRestored, onConnectionError]);

    // Tratar mudanças de estado de conexão
    useEffect(() => {
        const prevState = previousConnectionStateRef.current;
        const currentState = echoContext.connectionState;

        // Conexão restaurada: estava desconectado/conectando, agora conectado
        if (currentState === ConnectionState.CONNECTED && prevState !== ConnectionState.CONNECTED) {
            if (onConnectionRestoredRef.current) {
                log("Conexão restaurada, chamando onConnectionRestored");
                onConnectionRestoredRef.current();
            }
        }

        // Erro de conexão: estava conectado, agora desconectado
        if (currentState === ConnectionState.DISCONNECTED && prevState === ConnectionState.CONNECTED) {
            if (onConnectionErrorRef.current) {
                log("Conexão perdida, chamando onConnectionError");
                onConnectionErrorRef.current(new Error("Connection disconnected"));
            }
        }

        previousConnectionStateRef.current = currentState;
    }, [echoContext.connectionState]);

    // Subscrever aos eventos - APENAS subscreve aos eventos que têm handlers
    useEffect(() => {
        // Não subscrever se desativado ou sem canal
        if (!enabled || !channelName) {
            log("Subscrição ignorada - enabled:", enabled, "channelName:", channelName);
            return;
        }

        const currentEvents = eventsRef.current;

        // Não subscrever se não houver eventos
        if (!currentEvents || Object.keys(currentEvents).length === 0) {
            log("Subscrição ignorada - nenhum evento definido");
            return;
        }

        log("========== CONFIGURANDO CANAL ==========");
        log(`Canal: ${channelName}`);
        log(`Eventos a subscrever: ${Object.keys(currentEvents).join(', ')}`);
        log("=========================================");

        // Coletar funções de unsubscribe
        const unsubscribeFunctions: (() => void)[] = [];

        // Subscrever APENAS aos eventos que têm handlers definidos
        Object.entries(currentEvents).forEach(([eventName, handler]) => {
            if (handler) {
                log(`[SUBSCRIBE] Subscrevendo ao evento: ${eventName} no canal: ${channelName}`);
                const unsubscribe = echoContext.subscribe(channelName, eventName, (data: any) => {
                    // Sempre usar o handler mais recente da ref
                    const currentHandler = eventsRef.current?.[eventName];
                    if (currentHandler) {
                        log("========== EVENTO RECEBIDO VIA WEBSOCKET ==========");
                        log(`Canal: ${channelName}`);
                        log(`Evento: ${eventName}`);
                        log(`Timestamp: ${new Date().toISOString()}`);
                        log(`Dados:`, data);
                        log("===================================================");
                        currentHandler(data);
                    } else {
                        log(`[WARN] Handler não encontrado para evento: ${eventName}`);
                    }
                });
                unsubscribeFunctions.push(unsubscribe);
            }
        });

        // Função de cleanup
        return () => {
            log(`Limpando subscrições do canal: ${channelName}`);
            unsubscribeFunctions.forEach(unsubscribe => unsubscribe());
        };
    }, [channelName, enabled, echoContext]);

    return {
        echo: echoContext.echo,
        connectionState: echoContext.connectionState
    };
};
